/**
 * Predicate to filter arrays that are typed but may contain null/undefined/false values in order to render conditional items
 * e.g. const numbersOnly: number[] = [1, 2, null, undefined, false].filter(notEmpty)
 */
export function isPresent<TValue>(
  value: TValue | null | undefined | boolean,
): value is TValue {
  return value != null && value !== false;
}

export type Only<T, U> = {
  [P in keyof T]: T[P];
} & {
  [P in keyof U]?: never;
};

export type Either<T, U> = Only<T, U> | Only<U, T>;

export type NullableProperties<T> = {
  [P in keyof T]: T[P] | null;
};

export type RequiredNonNullableFields<T, K extends keyof T> = {
  [P in K]-?: NonNullable<T[P]>;
};

export const isEnum =
  <T extends { [s: string]: unknown }>(e: T) =>
  (token?: unknown): token is T[keyof T] =>
    Object.values(e).includes(token as T[keyof T]);

export const isArrayElement =
  <T extends string>(e: Readonly<T[]>) =>
  (token?: unknown): token is T =>
    e.includes(token as T);

/**
 * Returns a function that takes a value and returns an enum value if it
 * is a valid enum member, otherwise returns the fallback value.
 *
 * Handy for quickly narrowing a loose type to a specific enum type without
 * rewriting fallback logic repeatedly.
 */
export function enumOrFallback<
  FallbackType,
  EnumObject extends { [s: string]: string | number },
  TCoerceEmpty extends boolean = false,
>(enumObject: EnumObject, fallback: FallbackType, coerceEmpty: TCoerceEmpty) {
  return (
    candidate: typeof coerceEmpty extends true
      ? string | number | null | undefined
      : string | number,
  ): (typeof enumObject)[keyof EnumObject] | FallbackType =>
    isEnum(enumObject)(candidate) ? candidate : fallback;
}

/**
 * Returns a function that takes a value and returns an array element if it
 * is present in the array, otherwise returns the fallback value.
 *
 * Similar to enumOrFallback but works with arrays instead of enums.
 */
export function arrayElementOrFallback<
  T extends string,
  FallbackType,
  TCoerceEmpty extends boolean = false,
>(array: ReadonlyArray<T>, fallback: FallbackType, coerceEmpty: TCoerceEmpty) {
  return (
    candidate: typeof coerceEmpty extends true
      ? string | null | undefined
      : string,
  ): T | FallbackType =>
    isArrayElement(array)(candidate) ? candidate : fallback;
}

export const hasNonNullProp =
  <K extends PropertyKey>(key: K) =>
  <T extends Record<K, unknown | null>>(
    obj: T,
  ): obj is T & Record<K, NonNullable<T[K]>> =>
    obj[key] != null;

// Lodash's partition is not type the output correctly
export const partition = <T, S extends T>(
  arr: readonly T[],
  guard: (value: T) => value is S,
): [S[], Exclude<T, S>[]] => {
  const pass: S[] = [];
  const fail: Exclude<T, S>[] = [];

  for (const item of arr) {
    if (guard(item)) {
      pass.push(item);
    } else {
      fail.push(item as Exclude<T, S>);
    }
  }

  return [pass, fail];
};
